import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Platform,
  View,
  Text,
  FlatList,
  TouchableOpacity,
  Keyboard,
  Pressable,
  StyleSheet,
} from 'react-native';
import {TextInput, TouchableRipple, Portal} from 'react-native-paper';
import moment, {Moment} from 'moment';
import {useStyle} from 'src/providers/style';
import {Typography} from 'src/styles';
import {useDimensions} from '@react-native-community/hooks';

type Role = 'start' | 'end';

interface Props {
  label: string;
  value?: Date;
  onChange?: (d: Date) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string | any;

  role?: Role; // 'start' | 'end'
  anchorTime?: Date; // when role='end', used for duration suggestions
  intervalMinutes?: number; // start grid step (default 15)
  maxDurationMinutes?: number; // end suggestions cap (default 480=8h)
  suggestionDurations?: number[]; // explicit durations for end (wins over cap)
}

const DEFAULT_END_CAP = 480; // 8h
const DEFAULT_INTERVAL = 15;
const ITEM_HEIGHT = 40;
const PANEL_MAX_H = 280;

const fmt = (d?: Date) => (d ? moment(d).format('h:mma') : '');
const ucFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatDuration = (mins: number) => {
  if (mins < 60) {
    return `${mins} ${mins === 1 ? 'min' : 'mins'}`;
  }
  const hours = mins / 60;
  if (Number.isInteger(hours)) {
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }
  const rounded = Math.round(hours * 100) / 100; // max 2 decimals
  return `${rounded} hrs`;
};

const parseTypedTime = (raw: string, baseDay: Moment) => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) {
    return null;
  }

  const withMeridiem = ['h:mm a', 'hh:mm a', 'h a', 'ha'];
  for (const f of withMeridiem) {
    const m = moment(s, f, true);
    if (m.isValid()) {
      return baseDay
        .clone()
        .hour(m.hour())
        .minute(m.minute())
        .second(0)
        .millisecond(0);
    }
  }

  if (/^\d+$/.test(s)) {
    if (s.length <= 2) {
      const h = Number(s);
      if (h >= 0 && h <= 23) {
        return baseDay.clone().hour(h).minute(0).second(0).millisecond(0);
      }
    } else if (s.length === 3) {
      const h = Number(s.slice(0, 1));
      const m = Number(s.slice(1));
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return baseDay.clone().hour(h).minute(m).second(0).millisecond(0);
      }
    } else if (s.length === 4) {
      const h = Number(s.slice(0, 2));
      const m = Number(s.slice(2));
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return baseDay.clone().hour(h).minute(m).second(0).millisecond(0);
      }
    }
  }

  const fmts24 = ['H:mm', 'HH:mm', 'H', 'HH'];
  for (const f of fmts24) {
    const m = moment(s, f, true);
    if (m.isValid()) {
      return baseDay
        .clone()
        .hour(m.hour())
        .minute(m.minute())
        .second(0)
        .millisecond(0);
    }
  }

  const loose = moment(s, ['h:mm a', 'h a', 'H:mm', 'Hmm', 'hmm'], false);
  if (loose.isValid()) {
    return baseDay
      .clone()
      .hour(loose.hour())
      .minute(loose.minute())
      .second(0)
      .millisecond(0);
  }
  return null;
};

const minutesOfDay = (d: Date) => moment(d).hours() * 60 + moment(d).minutes();
const sameTimeOfDay = (a: Date, b: Date) => minutesOfDay(a) === minutesOfDay(b);

const buildStartSuggestions = (interval = DEFAULT_INTERVAL) => {
  const base = moment().startOf('day');
  const items: {label: string; value: Date}[] = [];
  for (let i = 0; i < 24 * 60; i += interval) {
    const t = base.clone().add(i, 'minutes');
    items.push({label: ucFirst(t.format('h:mma')), value: t.toDate()});
  }
  return items;
};

const buildEndSuggestions = (
  start: Moment,
  interval = DEFAULT_INTERVAL,
  maxMin = DEFAULT_END_CAP,
  explicit?: number[],
) => {
  const out: {label: string; value: Date; duration: number}[] = [];
  if (explicit && explicit.length) {
    for (const mins of explicit) {
      const end = start.clone().add(mins, 'minutes');
      out.push({
        label: `${ucFirst(end.format('h:mma'))} (${formatDuration(mins)})`,
        value: end.toDate(),
        duration: mins,
      });
    }
    return out;
  }
  const cap = Math.max(interval, Math.min(maxMin, 12 * 60));
  for (let mins = interval; mins <= cap; mins += interval) {
    const end = start.clone().add(mins, 'minutes');
    out.push({
      label: `${ucFirst(end.format('h:mma'))} (${formatDuration(mins)})`,
      value: end.toDate(),
      duration: mins,
    });
  }
  return out;
};

const TimeComboBox = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  role = 'start',
  anchorTime,
  intervalMinutes = DEFAULT_INTERVAL,
  maxDurationMinutes = DEFAULT_END_CAP,
  suggestionDurations,
}: Props) => {
  const styles = useStyle();
  const dims = useDimensions();

  const wrapperRef = useRef<any>(null); // anchor
  const inputRef = useRef<any>(null);
  const listRef = useRef<FlatList>(null);
  const remeasureTimer = useRef<NodeJS.Timeout | null>(null);

  const [text, setText] = useState<string>(fmt(value));
  const [current, setCurrent] = useState<Date | undefined>(value);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{top: number; left: number; width: number}>({
    top: 0,
    left: 0,
    width: 0,
  });

  // keyboard tracking (native)
  const [kbHeight, setKbHeight] = useState(0);
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    const onShow = (e: any) => {
      setKbHeight(e?.endCoordinates?.height ?? 0);
      setTimeout(measureAndPlace, 50);
    };
    const onHide = () => {
      setKbHeight(0);
      setTimeout(measureAndPlace, 50);
    };
    // renamed to avoid shadowing the prop `onChange`
    const onKbChange = (e: any) => {
      setKbHeight(e?.endCoordinates?.height ?? kbHeight);
      setTimeout(measureAndPlace, 50);
    };
    const s1 = Keyboard.addListener('keyboardDidShow', onShow);
    const s2 = Keyboard.addListener('keyboardDidHide', onHide);
    const s3 = Keyboard.addListener('keyboardDidChangeFrame', onKbChange);
    return () => {
      s1.remove();
      s2.remove();
      s3.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defaults if not provided
  useEffect(() => {
    if (value) {
      return;
    }
    if (role === 'start') {
      const roundedUp =
        Math.ceil(moment().minute() / intervalMinutes) * intervalMinutes;
      const m = moment().minute(roundedUp === 60 ? 0 : roundedUp);
      if (roundedUp === 60) {
        m.add(1, 'hour');
      }
      const d = m.second(0).millisecond(0).toDate();
      setCurrent(d);
      setText(fmt(d));
      onChange?.(d);
    } else {
      const base = anchorTime ? moment(anchorTime) : moment();
      const d = base
        .clone()
        .add(60, 'minutes')
        .second(0)
        .millisecond(0)
        .toDate();
      setCurrent(d);
      setText(fmt(d));
      onChange?.(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, anchorTime, intervalMinutes]);

  // Sync from parent
  useEffect(() => {
    if (!value) {
      return;
    }
    setCurrent(value);
    setText(fmt(value));
  }, [value]);

  // Enforce end >= start
  useEffect(() => {
    if (role !== 'end' || !anchorTime || !current) {
      return;
    }
    if (minutesOfDay(current) < minutesOfDay(anchorTime)) {
      const fixed = moment(anchorTime).add(60, 'minutes').toDate();
      setCurrent(fixed);
      setText(fmt(fixed));
      onChange?.(fixed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorTime]);

  const commit = (d: Date) => {
    let next = d;
    if (
      role === 'end' &&
      anchorTime &&
      minutesOfDay(d) < minutesOfDay(anchorTime)
    ) {
      next = moment(anchorTime).add(1, 'minute').toDate();
    }
    setCurrent(next);
    setText(fmt(next));
    onChange?.(next);
  };

  const measureAndPlace = () => {
    if (!wrapperRef.current) {
      return;
    }

    if (Platform.OS === 'web') {
      const n: any = wrapperRef.current;
      const rect =
        n?.getBoundingClientRect?.() ??
        n?._node?.getBoundingClientRect?.() ??
        null;
      if (!rect) {
        return;
      }
      const availBottom = window.innerHeight - rect.bottom;
      const flipUp = availBottom < PANEL_MAX_H;
      setPos({
        top: (flipUp ? rect.top - PANEL_MAX_H : rect.bottom) + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
      return;
    }

    // Native measurement in screen coords
    wrapperRef.current?.measureInWindow?.(
      (x: number, y: number, width: number, height: number) => {
        const screenH = dims.window.height;
        const safeBottom = screenH - kbHeight; // top of keyboard
        let top = y + height; // default below input
        if (top + PANEL_MAX_H > safeBottom - 8) {
          top = Math.max(8, y - PANEL_MAX_H);
        }
        setPos({top, left: x, width});
      },
    );
  };

  const openDropdown = () => {
    measureAndPlace();
    setOpen(true);

    // keep input focused on mobile so the keyboard stays up
    if (Platform.OS !== 'web') {
      setTimeout(() => inputRef.current?.focus?.(), 0);
    }

    if (remeasureTimer.current) {
      clearInterval(remeasureTimer.current);
    }
    remeasureTimer.current = setInterval(measureAndPlace, 60);
    setTimeout(() => {
      if (remeasureTimer.current) {
        clearInterval(remeasureTimer.current);
        remeasureTimer.current = null;
      }
    }, 360);

    setTimeout(() => {
      if (selectedIndex >= 0) {
        listRef.current?.scrollToIndex({index: selectedIndex, animated: false});
      }
    }, 0);
  };

  const closeDropdown = (commitTyped = true) => {
    setOpen(false);
    if (remeasureTimer.current) {
      clearInterval(remeasureTimer.current);
      remeasureTimer.current = null;
    }
    if (commitTyped) {
      const base = moment(anchorTime ?? current ?? new Date()).startOf('day');
      const parsed = parseTypedTime(text, base);
      if (parsed) {
        commit(parsed.toDate());
      } else {
        setText(fmt(current));
      }
    }
    if (Platform.OS !== 'web') {
      Keyboard.dismiss();
    }
  };

  // Suggestions
  const startSuggestions = useMemo(
    () => (role === 'start' ? buildStartSuggestions(intervalMinutes) : []),
    [role, intervalMinutes],
  );

  const endSuggestions = useMemo(() => {
    if (role !== 'end') {
      return [];
    }
    const base = moment(anchorTime ?? new Date());
    return buildEndSuggestions(
      base,
      intervalMinutes,
      maxDurationMinutes,
      suggestionDurations,
    );
  }, [
    role,
    anchorTime,
    intervalMinutes,
    maxDurationMinutes,
    suggestionDurations,
  ]);

  const data: {label: string; value: Date; duration?: number}[] =
    role === 'start' ? startSuggestions : endSuggestions;

  // Selected index (for highlight/scroll)
  const selectedIndex = useMemo(() => {
    if (!current) {
      return -1;
    }
    if (role === 'start') {
      const mod = minutesOfDay(current);
      return data.findIndex(it => minutesOfDay(it.value) === mod);
    }
    if (!anchorTime) {
      return -1;
    }
    const diff = moment(current).diff(moment(anchorTime), 'minutes');
    const durIdx = (data as any[]).findIndex((it: any) => it.duration === diff);
    if (durIdx >= 0) {
      return durIdx;
    }
    return data.findIndex(it => sameTimeOfDay(it.value, current));
  }, [data, role, current, anchorTime]);

  const typedOption = useMemo(() => {
    if (!text) {
      return null;
    }
    const base = moment(anchorTime ?? current ?? new Date()).startOf('day');
    const parsed = parseTypedTime(text, base);
    if (!parsed) {
      return null;
    }

    let candidate = parsed;
    if (
      role === 'end' &&
      anchorTime &&
      minutesOfDay(candidate.toDate()) < minutesOfDay(anchorTime)
    ) {
      candidate = moment(anchorTime).add(1, 'minute');
    }
    const candidateDate = candidate.toDate();
    const alreadyInList =
      data.findIndex(it => sameTimeOfDay(it.value, candidateDate)) !== -1;

    if (alreadyInList) {
      return null;
    }

    // renamed to avoid shadowing the prop `label`
    const optionLabel =
      role === 'end' && anchorTime
        ? `${candidate.format('h:mma')} (${formatDuration(
            candidate.diff(moment(anchorTime), 'minutes'),
          )})`
        : candidate.format('h:mma');

    return {label: ucFirst(optionLabel), value: candidateDate};
  }, [text, role, anchorTime, current, data]);

  const onKeyPress = (e: any) => {
    const key = e?.nativeEvent?.key || e?.key;
    if (key === 'Enter') {
      closeDropdown(true);
    }
  };

  const InputField = (
    <TextInput
      ref={inputRef}
      value={text}
      onChangeText={setText}
      onFocus={openDropdown}
      onBlur={() => {
        if (!open) {
          const base = moment(anchorTime ?? current ?? new Date()).startOf(
            'day',
          );
          const parsed = parseTypedTime(text, base);
          if (parsed) {
            commit(parsed.toDate());
          } else {
            setText(fmt(current));
          }
        }
      }}
      onKeyPress={onKeyPress}
      returnKeyType="done"
      blurOnSubmit={false}
      onSubmitEditing={() => closeDropdown(true)}
      mode="outlined"
      label={label + (required ? '*' : '')}
      placeholder={label + (required ? '*' : '')}
      right={
        <TextInput.Icon
          name={open ? 'menu-up' : 'menu-down'}
          onPress={() => (open ? closeDropdown(true) : openDropdown())}
        />
      }
      disabled={disabled}
      style={styles.input}
    />
  );

  return (
    <View>
      <TouchableRipple
        onPress={() => {
          openDropdown();
          if (Platform.OS !== 'web') {
            inputRef.current?.focus?.();
          }
        }}
        disabled={disabled}>
        <View ref={wrapperRef}>{InputField}</View>
      </TouchableRipple>

      {Platform.OS === 'web' ? (
        <Portal>
          {open && (
            <View
              style={[
                styles.positionFixed,
                styles.zIndex10000,
                {
                  top: pos.top,
                  left: pos.left,
                  width: pos.width || 280,
                },
              ]}>
              <TouchableOpacity
                activeOpacity={1}
                onPress={() => closeDropdown(true)}
                style={[
                  styles.positionFixed,
                  styles.bottom0,
                  styles.top0,
                  styles.right0,
                  styles.left0,
                ]}
              />
              <View
                style={[
                  styles.positionAbsolute,
                  styles.backgroundColorWhite,
                  styles.borderRadiusSM,
                  styles.elevation,
                  styles.overflowHidden,
                  styles.top0,
                  styles.right0,
                  styles.left0,
                  styles.maxHeight250,
                ]}>
                {typedOption ? (
                  <TouchableOpacity
                    onPress={() => {
                      commit(typedOption.value);
                      closeDropdown(false);
                    }}
                    style={[styles.paddingMVertical]}>
                    <Text style={[Typography.LABEL, styles.paddingLLeft]}>
                      {typedOption.label}
                    </Text>
                  </TouchableOpacity>
                ) : null}

                <FlatList
                  ref={listRef}
                  data={data}
                  keyExtractor={(_, i) => String(i)}
                  initialScrollIndex={
                    data.length ? Math.max(selectedIndex, 0) : undefined
                  }
                  getItemLayout={(_, index) => ({
                    length: ITEM_HEIGHT,
                    offset: ITEM_HEIGHT * index,
                    index,
                  })}
                  keyboardShouldPersistTaps="handled"
                  renderItem={({item, index}) => {
                    const selected = index === selectedIndex;
                    return (
                      <TouchableOpacity
                        onPress={() => {
                          commit(item.value);
                          closeDropdown(false);
                        }}
                        style={[
                          selected
                            ? styles.backgroundColorBlueOverlay
                            : styles.backgroundColorTransparent,
                        ]}>
                        <Text style={[Typography.LABEL, styles.paddingL]}>
                          {item.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  }}
                  ListEmptyComponent={
                    !typedOption ? (
                      <View style={[styles.paddingL]}>
                        <Text style={[Typography.P3]}>No suggestions</Text>
                      </View>
                    ) : null
                  }
                  style={[styles.maxHeight250]}
                />
              </View>
            </View>
          )}
        </Portal>
      ) : (
        <Portal>
          {open && (
            <View
              style={[
                StyleSheet.absoluteFillObject,
                styles.width,
                styles.flex,
                styles.elevation,
              ]}
              pointerEvents="box-none">
              <Pressable
                style={StyleSheet.absoluteFillObject}
                onPress={() => closeDropdown(true)}
              />
              <View
                style={[
                  {
                    top: pos.top,
                    left: pos.left,
                    width: pos.width || 280,
                  },
                  styles.positionAbsolute,
                  styles.backgroundColorWhite,
                  styles.borderRadiusSM,
                  styles.elevation,
                  styles.overflowHidden,
                ]}>
                {typedOption ? (
                  <TouchableOpacity
                    onPress={() => {
                      commit(typedOption.value);
                      closeDropdown(false);
                    }}
                    style={[styles.paddingMVertical]}>
                    <Text style={[Typography.LABEL, styles.paddingLLeft]}>
                      {typedOption.label}
                    </Text>
                  </TouchableOpacity>
                ) : null}

                <FlatList
                  ref={listRef}
                  data={data}
                  keyExtractor={(_, i) => String(i)}
                  initialScrollIndex={
                    data.length ? Math.max(selectedIndex, 0) : undefined
                  }
                  getItemLayout={(_, index) => ({
                    length: ITEM_HEIGHT,
                    offset: ITEM_HEIGHT * index,
                    index,
                  })}
                  keyboardShouldPersistTaps="always"
                  renderItem={({item, index}) => {
                    const selected = index === selectedIndex;
                    return (
                      <TouchableOpacity
                        onPress={() => {
                          commit(item.value);
                          closeDropdown(false);
                        }}
                        style={[
                          selected
                            ? styles.backgroundColorBlueOverlay
                            : styles.backgroundColorTransparent,
                        ]}>
                        <Text style={[Typography.LABEL, styles.paddingL]}>
                          {item.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  }}
                  ListEmptyComponent={
                    !typedOption ? (
                      <View style={[styles.paddingL]}>
                        <Text style={[Typography.P3]}>No suggestions</Text>
                      </View>
                    ) : null
                  }
                  style={styles.maxHeight250}
                />
              </View>
            </View>
          )}
        </Portal>
      )}

      {error ? (
        <Text style={[Typography.P3_BOLD, styles.textColorError]}>
          {String(error)}
        </Text>
      ) : null}
    </View>
  );
};

export default TimeComboBox;
